import type { COMMON_FRENCH_LOCALE } from "~ui/@application/locales/modules/common/common.fr.ts";

import { ECountryIso2Code, ECountryIso3Code } from "~application/constants/country.constant.ts";
import { ELocale } from "~application/constants/locale.constant.ts";
import { ERouteName } from "~application/constants/routes.constant.ts";
import { DomainErrorCode } from "~domain/global/errors/domain-error.ts";
import { ESitePhotoType } from "~domain/sites/models/site-photo-model.ts";
import { SubscriberMyVoltalisAccess } from "~domain/subscribers/models/subscriber-model.ts";

const myvoltalis: Record<SubscriberMyVoltalisAccess, string> = {
  [SubscriberMyVoltalisAccess.MY_VOLTALIS_2016]: "MyVoltalis 2016",
  [SubscriberMyVoltalisAccess.MY_VOLTALIS_2022]: "MyVoltalis 2022",
};

const breadcrumb: Record<ERouteName, string> = {
  [ERouteName.ROOT]: "Voltexploit",
  [ERouteName.AUTHENTICATION]: "Authentication",
  [ERouteName.AUTHENTICATION__LOGIN]: "Login",
  [ERouteName.AUTHENTICATION__LOGOUT]: "Logout",
  [ERouteName.SEARCH_SITE]: "Search site",
  [ERouteName.DEVICES_MODULATOR_DETAILS]: "Modulator",
  [ERouteName.DEVICES_PILOTS_DETAILS]: "Pilot",
  [ERouteName.DEVICES_THIRD_PARTY_SENSORS_DETAILS]: "Third party sensor",
  [ERouteName.SITES_ROOT]: "Site {siteId}",
  [ERouteName.SITES_OVERVIEW]: "Overview",
  [ERouteName.SITES_QUALITY_CONTROL]: "Quality control",
  [ERouteName.SITES_DEVICES]: "Devices",
  [ERouteName.SITES_GALLERY]: "Gallery",
  [ERouteName.SITES_WORKING_CONDITION]: "Working condition",
  [ERouteName.SITES_WORKING_CONDITION__ACQUISITION]: "Acquisition",
  [ERouteName.SITES_WORKING_CONDITION__MODULATION]: "Modulation",
};

const errors: Record<DomainErrorCode, string> = {
  [DomainErrorCode.BAD_REQUEST_ERROR]: "Bad Request",
  [DomainErrorCode.UNEXPECTED_ERROR]: "Unexpected Error",
  [DomainErrorCode.FORBIDDEN_ERROR]: "Access Denied",
  [DomainErrorCode.VALIDATION_FAILED_ERROR]: "Validation Error",
  [DomainErrorCode.FIELD_MISSING_ERROR]: "Missing Field",
  [DomainErrorCode.NOT_FOUND_ERROR]: "Not Found",
  [DomainErrorCode.FILE_UPLOAD_ERROR]: "Upload Error",
  [DomainErrorCode.TRANSPORT_ERROR]: "API Error",
  [DomainErrorCode.INVALID_ARGUMENT_ERROR]: "Invalid Argument",
  [DomainErrorCode.DEVICE_NOT_FOUND_ERROR]: "Device Not Found",
  [DomainErrorCode.DEVICE_ALREADY_ON_SITE_ERROR]: "Device Already On Site",
  [DomainErrorCode.DEVICE_NOT_LINKED_TO_SITE_ERROR]: "Device Not Linked To Site",
  [DomainErrorCode.DEVICE_HAS_WRONG_TYPE_ERROR]: "Incorrect Device Type",
  [DomainErrorCode.DEVICE_HAS_NO_HW_LINK_ERROR]: "Device Missing HW Link",
  [DomainErrorCode.VX_UNKNOWN_DEVICE_ERROR]: "Unknown Device",
  [DomainErrorCode.GATEWAY_NOT_LINKED_TO_SITE_ERROR]: "Gateway Not Linked To Site",
  [DomainErrorCode.GATEWAY_UNAVAILABLE_ERROR]: "Gateway Unavailable",
  [DomainErrorCode.DEVICE_HAS_WRONG_MODE_ERROR]: "Incorrect Device Mode",
  [DomainErrorCode.COMMUNICATION_STATE_NOT_FOUND_ERROR]: "Communication Error",
  [DomainErrorCode.GATEWAY_UPDATE_FAILED_ERROR]: "Gateway Update Failed",
  [DomainErrorCode.MISSING_OPERATOR_COMPANY_ERROR]: "Operator Missing Company",
  [DomainErrorCode.OPERATION_NOT_FOUND_ERROR]: "Operation Not Found",
  [DomainErrorCode.SITE_OPERATION_LINK_NOT_FOUND_ERROR]: "Site Not Linked To Operation",
  [DomainErrorCode.SITE_HAS_MULTIPLE_MISSIONS_ERROR]: "Site Has Multiple Missions",
  [DomainErrorCode.SITE_IS_ATTACHED_TO_MULTIPLE_OPERATIONS_ERROR]: "Site Linked To Multiple Operations",
  [DomainErrorCode.OPERATION_MISSION_NOT_FOUND_ERROR]: "Operation Mission Not Found",
};

const photoType: Record<ESitePhotoType, string> = {
  [ESitePhotoType.DELIVERY_POINT]: "MPAN",
  [ESitePhotoType.CONTRACT]: "Contract",
  [ESitePhotoType.BEFORE_INSTALL_CLOSED]: "Before Installation - Closed",
  [ESitePhotoType.BEFORE_INSTALL_OPENED]: "Before Installation - Opened",
  [ESitePhotoType.AFTER_INSTALL_CLOSED]: "After Installation - Closed",
  [ESitePhotoType.AFTER_INSTALL_OPENED]: "After Installation - Opened",
  [ESitePhotoType.OTHER]: "Other",
  [ESitePhotoType.RADIATORS]: "Heaters",
  [ESitePhotoType.INSTALLATION_SHEET_VOLTA_PAP]: "Installation Sheet VOLTAPAP",
  [ESitePhotoType.ELECTRICAL_BOARD]: "Electrical Board",
  [ESitePhotoType.INSTALLATION_SHEET_VOLT_INSTALL]: "Installation Sheet VOLTINSTALL",
  [ESitePhotoType.CASKET]: "Casket",
  [ESitePhotoType.INSTALL_BEFORE_INSTALL]: "Install - Before Installation",
  [ESitePhotoType.INSTALL_AFTER_WIRING]: "Install - After Wiring",
  [ESitePhotoType.INSTALL_AFTER_REASSEMBLY]: "Install - After Reassembly",
  [ESitePhotoType.UNINSTALL_BEFORE_UNINSTALL]: "Uninstall - Before Uninstall",
  [ESitePhotoType.UNINSTALL_AFTER_REASSEMBLY]: "Uninstall - After Reassembly",
  [ESitePhotoType.BEFORE_WIRING]: "Before Wiring",
  [ESitePhotoType.INITIAL_INVENTORY]: "Initial Inventory",
  [ESitePhotoType.APPOINTMENT_COMMITMENT_FORM]: "Appointment Commitment Form",
  [ESitePhotoType.ELECTRICITY_BILL]: "Electricity Bill",
  [ESitePhotoType.CEE_QUOTE]: "CEE Quote",
  [ESitePhotoType.NEW_IMPORT_INSTALLATION_SHEET]: "New Import Installation Sheet",
  [ESitePhotoType.PROGRAMMING_MODE_STICKER]: "Programming mode sticker",
  [ESitePhotoType.WIRE_MODE]: "Wire mode",
  [ESitePhotoType.NAME_PLATE]: "Nameplate",
  [ESitePhotoType.ELECTRICAL_BOARD_AND_HOUSING]: "Electrical Board & Housing",
  [ESitePhotoType.RADIATORS_AND_TOWEL_DRYERS]: "Radiators & Towel Dryers",
  [ESitePhotoType.HOT_WATER_TANK]: "Hot Water Tank",
  [ESitePhotoType.UNKNOWN]: "Unknown",
};

export const COMMON_ENGLISH_LOCALE = {
  myvoltalis: myvoltalis,
  breadcrumb: breadcrumb,
  errors: errors,
  languages: {
    [ELocale.FRENCH]: "Français",
    [ELocale.ENGLISH]: "English",
  },
  countries: {
    iso2: {
      [ECountryIso2Code.FR]: "France",
      [ECountryIso2Code.GB]: "Great Britain",
      [ECountryIso2Code.SE]: "Sweden",
      [ECountryIso2Code.FI]: "Finland",
      [ECountryIso2Code.ES]: "Spain",
      [ECountryIso2Code.PR]: "Portugal",
      [ECountryIso2Code.SA]: "Saudi Arabia",
      [ECountryIso2Code.AE]: "United Arab Emirates",
    },
    iso3: {
      [ECountryIso3Code.FRA]: "France",
      [ECountryIso3Code.GBR]: "Great Britain",
      [ECountryIso3Code.SWE]: "Sweden",
      [ECountryIso3Code.FIN]: "Finland",
      [ECountryIso3Code.ESP]: "Spain",
      [ECountryIso3Code.PRT]: "Portugal",
      [ECountryIso3Code.SAU]: "Saudi Arabia",
      [ECountryIso3Code.ARE]: "United Arab Emirates",
    },
  },
  photoType: photoType,
  misc: {
    seeMore: "See more",
    search: "Search...",
    searchBy: "Search by {field}...",
    emptyResults: "No results found...",
    modulator: "Modulator",
    pilot: "Pilot",
    size: {
      small: "Small",
      medium: "Medium",
      large: "Large",
    },
    sortBy: "Sort by",
    asc: "Ascending",
    desc: "Descending",
    yes: "Yes",
    no: "No",
  },
} satisfies typeof COMMON_FRENCH_LOCALE;
